import React, { useEffect, useMemo, useState } from "react";
import consoleApi from "../../services/consoleApiService";

/**
 * StudioAccounts — the Studio "Accounts" tab. Per-account post track record
 * sourced from Post Bridge: every account, how many posts it's received, when
 * it was last posted to, and an expandable history (caption, status, when,
 * image count) for each. Post Bridge deletes uploaded media after publishing,
 * so past posts show the caption as the record rather than a thumbnail.
 */

const accentFor = (username) => {
  let h = 0;
  for (let i = 0; i < (username || "").length; i += 1) h = (h * 31 + username.charCodeAt(i)) % 360;
  return `linear-gradient(135deg, hsl(${h} 70% 55%), hsl(${(h + 40) % 360} 75% 48%))`;
};
const initials = (u) => (u || "?").replace(/^@/, "").slice(0, 2).toUpperCase();

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const fmtAgo = (iso) => {
  if (!iso) return "no posts yet";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function PostRow({ post }) {
  const [open, setOpen] = useState(false);
  const when = post.scheduled_at || post.created_at;
  const cap = post.caption || "";
  const long = cap.length > 140;
  const thumbs = Array.isArray(post.media_urls) ? post.media_urls : [];
  return (
    <div className="rc-sa-post">
      {thumbs.length > 0 ? (
        <a className="rc-sa-thumb" href={thumbs[0]} target="_blank" rel="noopener noreferrer" title="Open full image">
          <img src={thumbs[0]} alt="" loading="lazy" />
          {thumbs.length > 1 && <span className="rc-sa-thumb-n">+{thumbs.length - 1}</span>}
        </a>
      ) : (
        <span className={`rc-sa-status rc-sa-${post.status || "posted"}`}>{post.status || "posted"}</span>
      )}
      <div className="rc-sa-post-body">
        <div className="rc-sa-post-when">
          {fmtWhen(when)}{post.scheduled_at ? " · scheduled" : ""}
          {thumbs.length > 0 && <span className={`rc-sa-status-inline rc-sa-${post.status || "posted"}`}>{post.status || "posted"}</span>}
        </div>
        {cap ? (
          <div className={`rc-sa-caption ${open ? "open" : ""}`} onClick={() => long && setOpen((v) => !v)} role={long ? "button" : undefined}>
            {open || !long ? cap : `${cap.slice(0, 140)}…`}
            {long && <span className="rc-sa-more">{open ? " show less" : " show more"}</span>}
          </div>
        ) : (
          <div className="rc-sa-caption rc-sa-empty">No caption</div>
        )}
      </div>
      <span className="rc-sa-media">{post.media_count || 0} 🖼</span>
    </div>
  );
}

function AccountCard({ acct }) {
  const [open, setOpen] = useState(false);
  const has = acct.post_count > 0;
  return (
    <div className={`rc-sa-card ${has ? "" : "is-empty"}`}>
      <button type="button" className="rc-sa-acct" onClick={() => has && setOpen((v) => !v)}>
        <span className="rc-sa-avatar" style={{ background: accentFor(acct.username) }}>{initials(acct.username)}</span>
        <span className="rc-sa-meta">
          <span className="rc-sa-handle">@{acct.username}</span>
          <span className="rc-sa-platform">{acct.platform}</span>
        </span>
        <span className="rc-sa-counts">
          <span className="rc-sa-count">{acct.post_count}</span>
          <span className="rc-sa-count-label">{acct.post_count === 1 ? "post" : "posts"}</span>
        </span>
        <span className="rc-sa-last">{fmtAgo(acct.last_activity)}</span>
        <span className={`rc-sa-chev ${open ? "open" : ""}`}>{has ? "▾" : ""}</span>
      </button>
      {open && has && (
        <div className="rc-sa-posts">
          {acct.posts.map((p, i) => <PostRow key={p.id || i} post={p} />)}
        </div>
      )}
    </div>
  );
}

export default function StudioAccounts({ active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    consoleApi.content
      .studioHistory()
      .then((d) => { setData(Array.isArray(d) ? d : []); setLoadedOnce(true); })
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load history"))
      .finally(() => setLoading(false));
  };

  // Lazy-load when the tab first becomes active.
  useEffect(() => { if (active && !loadedOnce && !loading) load(); /* eslint-disable-next-line */ }, [active]);

  const totals = useMemo(() => {
    const accts = data || [];
    return { accounts: accts.length, posts: accts.reduce((s, a) => s + (a.post_count || 0), 0) };
  }, [data]);

  return (
    <div className="rc-sa">
      <style>{SA_CSS}</style>
      <div className="rc-sa-head">
        <p className="rc-sa-sub">
          {data ? `${totals.accounts} accounts · ${totals.posts} posts on record` : "Track record per account — what was posted, and when."}
        </p>
        <button type="button" className="rc-sa-ghost" onClick={load} disabled={loading}>{loading ? "Loading…" : "↻ Refresh"}</button>
      </div>

      {error ? <div className="rc-sa-msg rc-sa-err">{error}</div>
        : loading && !data ? <div className="rc-sa-msg">Loading post history…</div>
        : !data || data.length === 0 ? <div className="rc-sa-msg">No connected accounts.</div>
        : (
          <div className="rc-sa-list">
            {data.map((a) => <AccountCard key={a.id} acct={a} />)}
          </div>
        )}
    </div>
  );
}

const SA_CSS = `
  .rc-sa { color:var(--console-ink); }
  .rc-sa-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; }
  .rc-sa-sub { color:var(--console-muted); font-size:13.5px; margin:0; }
  .rc-sa-ghost { background:var(--console-surface); color:var(--console-ink); border:1px solid var(--console-border); border-radius:10px; padding:8px 13px; font-size:13px; font-weight:600; cursor:pointer; box-shadow:var(--console-shadow); }
  .rc-sa-ghost:hover { background:var(--console-s2); }
  .rc-sa-msg { background:var(--console-surface); border:1px solid var(--console-border); border-radius:14px; padding:22px; color:var(--console-muted); box-shadow:var(--console-shadow); }
  .rc-sa-err { color:var(--console-red); }
  .rc-sa-list { display:flex; flex-direction:column; gap:12px; }

  .rc-sa-card { background:var(--console-surface); border:1px solid var(--console-border); border-radius:14px; box-shadow:var(--console-shadow); overflow:hidden; }
  .rc-sa-card.is-empty { opacity:.72; }
  .rc-sa-acct { width:100%; display:flex; align-items:center; gap:13px; padding:14px 16px; background:none; border:none; cursor:pointer; text-align:left; }
  .rc-sa-card.is-empty .rc-sa-acct { cursor:default; }
  .rc-sa-avatar { width:42px; height:42px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:14px; color:#fff; }
  .rc-sa-meta { display:flex; flex-direction:column; min-width:0; }
  .rc-sa-handle { font-weight:700; font-size:14px; color:var(--console-ink); }
  .rc-sa-platform { font-size:11.5px; color:var(--console-muted); text-transform:capitalize; }
  .rc-sa-counts { margin-left:auto; text-align:right; display:flex; flex-direction:column; line-height:1.1; }
  .rc-sa-count { font-size:19px; font-weight:800; color:var(--console-ink); }
  .rc-sa-count-label { font-size:11px; color:var(--console-muted); }
  .rc-sa-last { width:96px; text-align:right; font-size:12.5px; color:var(--console-muted); }
  .rc-sa-chev { width:18px; text-align:center; color:var(--console-dim); transition:transform .15s; }
  .rc-sa-chev.open { transform:rotate(180deg); }

  .rc-sa-posts { border-top:1px solid var(--console-line2); background:var(--console-bg); padding:6px 10px 10px; }
  .rc-sa-post { display:flex; gap:12px; align-items:flex-start; padding:11px 6px; border-bottom:1px solid var(--console-line2); }
  .rc-sa-post:last-child { border-bottom:none; }
  .rc-sa-thumb { position:relative; flex-shrink:0; width:48px; height:48px; border-radius:8px; overflow:hidden; border:1px solid var(--console-border); display:block; background:var(--console-s2); }
  .rc-sa-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
  .rc-sa-thumb-n { position:absolute; right:2px; bottom:2px; background:rgba(0,0,0,.66); color:#fff; font-size:9px; font-weight:800; padding:1px 4px; border-radius:5px; }
  .rc-sa-status-inline { margin-left:8px; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; padding:2px 7px; border-radius:999px; }
  .rc-sa-status { flex-shrink:0; font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; padding:3px 8px; border-radius:999px; margin-top:1px; }
  .rc-sa-posted { background:rgba(16,185,129,.14); color:#0e9f6e; }
  .rc-sa-scheduled { background:var(--console-accent-bg); color:var(--console-accent); }
  .rc-sa-draft { background:var(--console-s3); color:var(--console-muted); }
  .rc-sa-failed, .rc-sa-error { background:rgba(229,62,62,.12); color:#c33; }
  .rc-sa-post-body { flex:1; min-width:0; }
  .rc-sa-post-when { font-size:12px; color:var(--console-muted); font-weight:600; margin-bottom:3px; }
  .rc-sa-caption { font-size:13.5px; color:var(--console-ink); line-height:1.5; white-space:pre-wrap; word-break:break-word; }
  .rc-sa-caption[role="button"] { cursor:pointer; }
  .rc-sa-empty { color:var(--console-dim); font-style:italic; }
  .rc-sa-more { color:var(--console-accent); font-weight:600; font-size:12.5px; }
  .rc-sa-media { flex-shrink:0; font-size:12px; color:var(--console-muted); white-space:nowrap; margin-top:2px; }
`;
